import { useState, useContext, useEffect, useMemo } from 'react';
import ROUTES from '../../../routes';
import {
  formatDate
} from '../../../util/format';
import useFormState from '../../../hooks/useFormState';
import guestPaymentFormValidationRules, {
  Model as PaymentFormModel,

} from './guest-payment-form/GuestPaymentForm.rules';
import guestPaymentAuthFormValidationRules, {
  Model as PaymentAuthFormModel,
} from './guest-payment-form/GuestPaymentAuthForm.rules';
import { useApolloClient, useMutation } from '@apollo/react-hooks';
import { useTranslation } from '../../../hooks/useTranslation';
import { GuestPaymentAccount, TransactionResults } from './GuestPaymentTypes';
import useSessionTimeout from '../../../hooks/useSessionTimeout';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import { NotificationsContext } from '../../../providers/NotificationsProvider';
import { PaymentProfileInfo } from '../../paymentus/types';
import moment from 'moment-timezone';
import { InvoiceQuickPayDetails } from '../../../__generated__/pge-types';
import usePgeQuery from '../../../hooks/usePgeQuery';
import gql from 'graphql-tag';
import { navigate } from '@reach/router';

const getFormattedDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = (1 + date.getMonth()).toString().padStart(2, '0');
  const day = date
    .getDate()
    .toString()
    .padStart(2, '0');

  return month + '/' + day + '/' + year;
};

const defaultGuestPaymentAccount: GuestPaymentAccount = {
  encryptedPersonId: '',
  dueDate: '',
  invoiceId: '',
  partialAddressLineOne: '',
  projectNumber: '',
  amountDue: '',
  paymentFee: ''
};

const defaultPaymentFormValues: PaymentFormModel = {
  amount: '',
};

const defaultPaymentAuthFormValues: PaymentAuthFormModel = {
  fullName: '',
};

export const submitPaymentMutation = gql`
  mutation invoiceMakePayment($payload: InvoicePaymentInputList!) {
    invoiceMakePayment(payload: $payload) {
      paymentConfirmation {
        email
        cardType
        confirmationId
        digitalWalletEmail        
        isSuccess
        lastFourDigitsCardNumber
        paymentAmount
        paymentMethod
        invoiceId
        errorReason
        errorMessage
        paymentFeeAmount
      }
    }
  }
`;

const INVOICE_PAYMENT_QUICK_PAY_QUERY = gql`  
  query InvoiceQuickPaymentDetails($encryptedInvoiceInfo: String!) {
    invoiceQuickPaymentDetails(encryptedInvoiceInfo: $encryptedInvoiceInfo) {
      encryptedPersonId
      invoiceDetails {
        companyAddress
        company
        billsList {
          amountDue
          encryptedBillId
          billId
          billAmount
          dueDate
          projectAddress
          projectNum
          status
          billId
        }
      }      
    }
  }
`;

export const GET_PAYMENT_PROFILE_FEE = gql`
  query GetPaymentFee($payload: PaymentFeeRequest!) {
    getPaymentFee(payload: $payload) {
      profileFeeList {
          convenienceFee
          paymentAmount
          totalAmount
          token
        }
      }
  }
`;


export enum IneligibilityReason {
  undefined,
  'NoToken',
  'Other',
  'NoInvoiceFound'
}


function getToken(querySearchParam: string): string {
  const x = querySearchParam.split('request=',2);
  console.log('querySearchParam',querySearchParam);
 return x.length === 2 ? x[1] : '';  
}

export default () => {
  const twentyMinutes = 1000 * 60 * 20;
  useSessionTimeout(twentyMinutes, ROUTES.HOME);
  const notificationContext = useContext(NotificationsContext);
  const { wrapWithLoader } = useWrapWithLoader();
  const apolloClient = useApolloClient();
  const { t } = useTranslation();
  const PstTime = moment()
    .tz('Canada/Pacific')
    .format('MM/DD/yyyy');

  const token = useMemo(() => {
     return typeof window !== 'undefined'
       ? getToken(window.location.search)
       : '';
   }, []);
  const [guestPaymentAccount, setGuestPaymentAccount] = useState<
    GuestPaymentAccount
  >(defaultGuestPaymentAccount);
 
  

  const { loading: invoiceLoading, data, error } = usePgeQuery<{
    invoiceQuickPaymentDetails: InvoiceQuickPayDetails;
    }>(INVOICE_PAYMENT_QUICK_PAY_QUERY, {
      variables: {
          encryptedInvoiceInfo: token,        
      },
      skip: !token,
      errorPolicy: 'all',
    });

  

  useEffect(() => {
    const fetchData = async () => {
      if (data?.invoiceQuickPaymentDetails) {
        setGuestPaymentAccount(prevState => ({
          ...prevState,
          encryptedBillId: data?.invoiceQuickPaymentDetails?.invoiceDetails?.billsList![0]?.encryptedBillId!,
          encryptedPersonId: data?.invoiceQuickPaymentDetails?.encryptedPersonId,
          dueDate: data?.invoiceQuickPaymentDetails?.invoiceDetails?.billsList![0]?.dueDate,
          invoiceId: data?.invoiceQuickPaymentDetails?.invoiceDetails?.billsList![0]?.billId!,
          partialAddressLineOne: data?.invoiceQuickPaymentDetails?.invoiceDetails?.billsList![0]?.projectAddress!,
          projectNumber: data?.invoiceQuickPaymentDetails?.invoiceDetails?.billsList![0]?.projectNum!,
          amountDue: data?.invoiceQuickPaymentDetails?.invoiceDetails?.billsList![0]?.amountDue?.toString()!,
        }));
           
       
        guestPaymentForm.reset({
          ...guestPaymentForm.values,
          amount: data?.invoiceQuickPaymentDetails?.invoiceDetails?.billsList![0]?.amountDue?.toString()!,
        });
      }
    };
    void fetchData();
  }, [data?.invoiceQuickPaymentDetails, invoiceLoading]);

  const guestPaymentForm = useFormState(defaultPaymentFormValues, {
    validate: guestPaymentFormValidationRules,
    validationContext: {
      apolloClient,
      t,
      maximumAmount: Number(guestPaymentAccount?.amountDue),
    },
  });
  const guestPaymentAuthForm = useFormState(defaultPaymentAuthFormValues, {
    validate: guestPaymentAuthFormValidationRules,
    validationContext: {
      apolloClient,
      t,    
      
    },
  });

  const clearGuestPaymentData = (): void => {
    setGuestPaymentAccount(defaultGuestPaymentAccount);
    guestPaymentForm.reset(defaultPaymentFormValues);
    guestPaymentAuthForm.reset(defaultPaymentAuthFormValues);
  };

  const [isInFlight, setIsInFlight] = useState(false);
  const [transactionResults, setTransactionResults] = useState<
    TransactionResults
  >({
    isSuccessful: false,
    amount: '',
    confirmationNumber: '',
    notificationContactValue: '',
  });

  function setErrorNotification(
    showNotification: boolean,
    errorMessage?: string,
  ) {
    const message = showNotification
      ? Boolean(errorMessage)
        ? t(errorMessage!)
        : t('GENERIC_ERROR_NOTIFICATION_MESSAGE_BODY')
      : null;
    notificationContext.setState({
      isOpen: showNotification,
      message: showNotification ? message : undefined,
      severity: showNotification ? 'error' : undefined,
    });
  }

  type Eligibility = {
    isEligible: boolean;
    ineligibilityReason: IneligibilityReason;
  };
  
   // Check for no token ineligibility
   const eligibility: Eligibility = {
    isEligible: Boolean(token),
    ineligibilityReason: !token
      ? IneligibilityReason.NoToken
      : IneligibilityReason.undefined,
  };

  //in case of error, instead of redirecting to error page, set
  //eligibility false so user is redirected to pay bill (via login)
  if (error) {
    eligibility.isEligible = false;
    eligibility.ineligibilityReason = IneligibilityReason.NoInvoiceFound;
  }

  
  const [submitGuestPayment, {}] = useMutation(submitPaymentMutation, {
        
    variables: {
      payload: {
        encryptedPersonId: guestPaymentAccount?.encryptedPersonId,
        guestEmailAddress: guestPaymentForm.values.guestEmail,
        payment:[{
          encryptedPersonId: guestPaymentAccount?.encryptedPersonId!,
          invoiceId: guestPaymentAccount?.invoiceId!,
          projectAddress: guestPaymentAccount?.partialAddressLineOne,
          projectNum: guestPaymentAccount?.projectNumber,
          tokenId: guestPaymentForm.values.paymentProfile?.profile.token,
          paymentAmount: Number(Number(guestPaymentForm.values.amount).toFixed(2)),
          paymentDate: formatDate(new Date(PstTime), 'MM/dd/yyyy'),
        }]
      }           
  }});

  const handleOnGuestPayNext = async () => {
   await wrapWithLoader(
     guestPaymentForm.submit(async () => {
         await getPaymentFee();
      }),
   )();
  };

  const handleOnGuestPayBack = async (): Promise<void> => {
    await navigate(ROUTES.INVOICE_PAYMENT_GUEST_PAY);
  };

  
  const handleOnSaveProfile = wrapWithLoader(
    async (profile: PaymentProfileInfo) => {
      guestPaymentForm.values.paymentProfile = profile;     
    },
  );

  const getPaymentFee = async (): Promise<void> => {     
      const queryResult = await apolloClient.query({
        query: GET_PAYMENT_PROFILE_FEE,
        variables: {
          payload:{
            paymentList: 
            {            
              token: guestPaymentForm?.values?.paymentProfile?.profile?.token,
              type: guestPaymentForm?.values?.paymentProfile?.profile?.type?.toString(),
              invoiceId:guestPaymentAccount?.invoiceId,
              paymentAmount:  Number(guestPaymentForm?.values?.amount)
            }
          }
        }
      });
  
      const guestPaymentFee = queryResult?.data?.getPaymentFee?.profileFeeList;
      setGuestPaymentAccount(prevState => ({
        ...prevState,
        paymentFee:guestPaymentFee
      }));
      
      await navigate(ROUTES.INVOICE_PAYMENT_GUEST_PAY_PAYMENT_CONFIRMATION);
     };
  const handleOnGuestPayCancelFromVerification = wrapWithLoader(async e => {
    e.preventDefault();
    clearGuestPaymentData();
    await navigate(ROUTES.INVOICE_PAYMENT_GUEST_PAY);
  });

  const handleOnGuestPaymentSubmit = wrapWithLoader(
    guestPaymentAuthForm.submit(async () => {
      try {
        setIsInFlight(true);

        const response = await submitGuestPayment();
        const submitResults = response?.data?.invoiceMakePayment?.paymentConfirmation[0];
        const success = Boolean(submitResults?.isSuccess);
        
        setTransactionResults({
          isSuccessful: success,
          amount: success ? submitResults?.paymentAmount : '',
          confirmationNumber: success ? submitResults?.confirmationId : '',
          notificationContactValue:
              success ? submitResults?.email : '',
          paymentFee: success ? submitResults?.paymentFeeAmount : '',
          invoiceId: success ? submitResults?.invoiceId : '',
        });

        if (success) {
        //  clearGuestPaymentData();          
          //typeof window !== 'undefined' &&
            //window.history.pushState(null, '', ROUTES.INVOICE_PAYMENT_GUEST_PAY);

          await navigate(ROUTES.INVOICE_PAYMENT_GUEST_PAY_PAYMENT_SUCCESS);
        } else {
          window.scrollTo(0, 0);
          setErrorNotification(true, submitResults.errorMessage);
        }
      } catch {
        window.scrollTo(0, 0);
        setErrorNotification(true);
      } finally {
        setIsInFlight(false);
      }
    }),
  );

  const handleDoneClick = async (): Promise<void> => {
    await navigate(ROUTES.INVOICE_PAYMENT_PORTAL_SIGN_IN);
  };

  
  return {
    invoiceLoading,    
    guestPaymentAccount,
    guestPaymentForm,
    handleOnGuestPayNext,
    paymentDate: formatDate(new Date(PstTime), 'MM/dd/yyyy'),
    handleOnGuestPaymentSubmit,
    isInFlight,
    transactionResults,
    handleDoneClick,
    clearGuestPaymentData,
    handleOnGuestPayBack,
    handleOnSaveProfile,    
    handleOnGuestPayCancelFromVerification,
    guestPaymentAuthForm,
    eligibility,
  };
};
